import { useEffect } from "react";
import { Link } from "react-router-dom";
import { trackViewContent } from "../utils/metaPixel";
import { 
  Heart,
  Target,
  Eye,
  Handshake,
  Users,
  Wrench,
  DollarSign,
  FileText,
  Clock,
  Check,
  Phone,
  ArrowRight,
  Home,
  Star
} from "lucide-react";

const KI_LOGO = "/images/ki-logo.png";

const InvestingInYourTomorrow = () => {
  // Track ViewContent when page loads (runs once on mount)
  useEffect(() => {
    trackViewContent({
      content_name: 'Investing In Your Tomorrow',
      content_category: 'About Page'
    });
  }, []);

  const benefits = [
    { icon: Wrench, text: "No Repairs" },
    { icon: DollarSign, text: "No Commissions" },
    { icon: FileText, text: "No Closing Costs" },
    { icon: Clock, text: "Fast Closings" }
  ];

  const promises = [
    "We will treat you with respect and compassion.",
    "We will communicate clearly and honestly.",
    "We will give you fair, no-obligation offers.",
    "We will close when you're ready.",
    "We will make the process simple from start to finish."
  ];

  const investingMeans = [
    { highlight: "More", text: "financial freedom." },
    { highlight: "Less", text: "stress and uncertainty." },
    { highlight: "More", text: "time for what matters most." },
    { highlight: "A brighter", text: "future for you and your family." }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation Bar */}
      <nav className="bg-[#0A0A0A] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src={KI_LOGO} alt="Knotts Investments" className="h-10 w-auto" />
              <span className="text-white font-semibold hidden sm:block">Knotts Investments LLC</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-[#A3A3A3] hover:text-white transition-colors text-sm">Home</Link>
              <Link 
                to="/sell-my-house" 
                className="btn-gold px-4 py-2 text-sm"
                data-testid="nav-cta"
              >
                Get Cash Offer
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/10 to-transparent"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <img 
            src={KI_LOGO} 
            alt="Knotts Investments LLC" 
            className="h-20 sm:h-24 w-auto mx-auto mb-6"
          />
          <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">Knotts Investments LLC</p>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
            Investing <span className="gold-text">In Your Tomorrow</span>
          </h1>
          
          <p className="text-xl sm:text-2xl text-[#D4AF37] font-semibold mb-8">
            Real Solutions. Real People. Real Results.
          </p>
          
          <p className="text-lg text-[#A3A3A3] max-w-2xl mx-auto leading-relaxed">
            At Knotts Investments LLC, we believe every challenge has a solution and every homeowner deserves options. 
            Our mission is simple: to help you move forward with confidence—today—so you can create a 
            <span className="text-[#D4AF37]"> better tomorrow</span> for you and your loved ones.
          </p>
        </div>
      </header>

      {/* Benefits Bar */}
      <section className="bg-[#141414] border-y border-[#D4AF37]/30 py-6">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-6 sm:gap-12">
            {benefits.map((benefit, index) => (
              <div key={benefit.text} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                  <benefit.icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <span className="text-white font-medium">{benefit.text}</span>
                {index < benefits.length - 1 && (
                  <div className="hidden sm:block w-px h-8 bg-[#D4AF37]/30 ml-6"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Two Column Grid */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            
            {/* Left Column */}
            <div className="space-y-12">
              
              {/* Why We Do What We Do */}
              <article className="about-card p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">
                    <span className="text-[#D4AF37]">Why</span> We Do What We Do
                  </h2>
                </div>
                <p className="text-[#A3A3A3] leading-relaxed text-lg">
                  We understand that life happens. Inherited properties, financial hardships, relocations, 
                  vacant homes, and unexpected situations can turn a house into a heavy burden.
                </p>
                <p className="text-[#A3A3A3] leading-relaxed text-lg mt-4">
                  When that happens, we&apos;re here to lift that burden so you can move forward.
                </p>
              </article>

              {/* Our Mission */}
              <article className="about-card p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <Target className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">
                    <span className="text-[#D4AF37]">Our</span> Mission
                  </h2>
                </div>
                <p className="text-[#A3A3A3] leading-relaxed text-lg">
                  To provide honest, respectful, and reliable solutions for homeowners who need to sell. 
                  We buy houses as-is, offer fair cash offers, and close on your timeline—making the 
                  process simple, stress-free, and transparent.
                </p>
              </article>

              {/* Our Promise */}
              <article className="about-card p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <Handshake className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">
                    <span className="text-[#D4AF37]">Our</span> Promise To You
                  </h2>
                </div>
                <ul className="space-y-3">
                  {promises.map((promise) => (
                    <li key={promise} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-1" />
                      <span className="text-[#A3A3A3] text-lg">{promise}</span>
                    </li>
                  ))}
                </ul>
              </article>
            </div>

            {/* Right Column */}
            <div className="space-y-12">
              
              {/* What Investing Means */}
              <article className="about-card p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <Star className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">
                    <span className="text-[#D4AF37]">What</span> &ldquo;Investing In Your Tomorrow&rdquo; Means
                  </h2>
                </div>
                <p className="text-[#A3A3A3] leading-relaxed text-lg mb-4">
                  It means we don&apos;t just buy houses—we help create new beginnings.
                </p>
                <p className="text-[#A3A3A3] leading-relaxed text-lg mb-6">
                  It means we invest in solutions today so you can invest in your tomorrow.
                </p>
                <ul className="space-y-3">
                  {investingMeans.map((item) => (
                    <li key={item.text} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-[#D4AF37] rounded-full"></div>
                      <span className="text-lg">
                        <span className="text-[#D4AF37] font-semibold">{item.highlight}</span>
                        <span className="text-[#A3A3A3]"> {item.text}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </article>

              {/* Our Vision */}
              <article className="about-card p-8">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                    <Eye className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">
                    <span className="text-[#D4AF37]">Our</span> Vision
                  </h2>
                </div>
                <p className="text-[#A3A3A3] leading-relaxed text-lg">
                  To be the most trusted home buying company in North Carolina and beyond—known for our 
                  integrity, our solutions, and the positive impact we create for homeowners and communities.
                </p>
              </article>

              {/* Callout Box */}
              <div className="bg-gradient-to-r from-[#D4AF37] to-[#E5C158] p-8 text-center">
                <p className="text-black text-lg font-medium mb-2">We Invest In Properties.</p>
                <p className="text-black text-2xl sm:text-3xl font-bold">
                  But More Importantly,<br />We Invest In People.
                </p>
              </div>
            </div>
          </div>

          {/* Personal Message from Rickey */}
          <section className="mt-16 lg:mt-24">
            <div className="bg-[#141414] border border-[#D4AF37]/30 p-8 lg:p-12 relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent"></div>
              
              <div className="flex items-start gap-4 mb-6">
                <Heart className="w-8 h-8 text-[#D4AF37] flex-shrink-0" />
              </div>
              
              <p className="text-[#A3A3A3] text-lg lg:text-xl leading-relaxed mb-6">
                Your situation is unique, and so is our approach. We take the time to listen, understand, 
                and create a solution that works for you. When you win, we win. That&apos;s what 
                <span className="text-[#D4AF37] font-semibold"> &ldquo;Investing In Your Tomorrow&rdquo;</span> is all about—helping you take the next best step 
                toward <span className="text-[#D4AF37]">the life you deserve</span>.
              </p>
              
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <p className="text-[#D4AF37] text-2xl font-script italic">Rickey Knotts</p>
                  <p className="text-[#A3A3A3] text-sm">Founder & CEO, Knotts Investments LLC</p>
                </div>
              </div>
            </div>
          </section>

          {/* Testimonial-style Statement */}
          <section className="mt-16 text-center">
            <div className="max-w-3xl mx-auto">
              <div className="text-6xl text-[#D4AF37]/30 mb-4">&ldquo;</div>
              <blockquote className="text-xl lg:text-2xl text-white italic leading-relaxed mb-4">
                Every house has a story. Every homeowner deserves respect. Whether you sell to us or not, 
                we want you to make the decision that&apos;s best for your future.
              </blockquote>
              <div className="text-6xl text-[#D4AF37]/30 rotate-180">&rdquo;</div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="mt-16 lg:mt-24 text-center">
            <div className="bg-[#0A0A0A] border-2 border-[#D4AF37] p-8 lg:p-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Ready to Take the Next Step?
              </h2>
              <p className="text-[#A3A3A3] text-lg mb-8 max-w-2xl mx-auto">
                Get a free, no-obligation cash offer for your property. No pressure. No hassle. 
                Just honest solutions.
              </p>
              
              <Link 
                to="/sell-my-house"
                className="btn-gold px-10 py-5 text-xl font-bold inline-flex items-center gap-3"
                data-testid="about-cta"
              >
                Get Your Free Cash Offer
                <ArrowRight className="w-5 h-5" />
              </Link>
              
              <p className="mt-8 text-[#A3A3A3]">
                Or call us directly:{" "}
                <a href="tel:704-930-0403" className="text-[#D4AF37] font-bold hover:text-[#E5C158]">
                  (704) 930-0403
                </a>
              </p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] border-t border-[#D4AF37]/30 py-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src={KI_LOGO} alt="Knotts Investments" className="h-8 w-auto" />
            <span className="text-white font-semibold">Knotts Investments LLC</span>
          </div>
          <p className="text-[#D4AF37] text-sm tracking-widest mb-4">— Investing In Your Tomorrow —</p>
          <div className="flex items-center justify-center gap-6 text-sm text-[#A3A3A3]">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <span>|</span>
            <Link to="/terms-conditions" className="hover:text-white transition-colors">Terms & Conditions</Link>
          </div>
          <p className="text-[#666] text-sm mt-4">
            © {new Date().getFullYear()} Knotts Investments LLC. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default InvestingInYourTomorrow;
